import React, { useState, useEffect, useMemo } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import '../pagination/pagination.css';
import '../../Assets/Style/style.css';
import Pagination from '../pagination/pagination';
import LeaseApiService from '../../services/apiService';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { FaRegEye, FaTrash, FaFileExport } from 'react-icons/fa';
import { HiPencil } from 'react-icons/hi2';
import { IoAddSharp } from 'react-icons/io5';
import Modal from 'react-bootstrap/Modal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useSelector } from 'react-redux';
import { getRole } from '../../../Redux/selecters';
import PropertyMasterCreate from './propertyCreate';
import PropertyMasterUpdate from './updateProperty';
import ColumnFilterHeader from './columnFilterHeader';
import RowActions from '../shared/rowActions';
import SummaryStrip, { countBy, countDistinct, totalFrom } from '../shared/summaryStrip';
import '../customerMaster/customerListing.css';
import { FiDownload, FiPlus, FiRotateCcw, FiSearch } from 'react-icons/fi';
import RangeChips from '../shared/rangeChips';
import { useRange, fetchRanged, useCardFilter, valueTiles } from '../shared/rangedList';

const PropertyListing = () => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState();
  const [selectedRow, setSelectedRow] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [FilterTab, setFilterTab] = useState(true);
  const [formModal, setFormModal] = useState({ mode: null, data: null });
  const role = useSelector(getRole);
  const isAdmin = role === 'Admin' || role === 'admin';
  const [columnFilters, setColumnFilters] = useState({});

  const [formData, setFormData] = useState({
    community: '',
    propertyName: '',
  });

  useEffect(() => {
    getItems();
  }, []);

  const { range, setRange, cache: rangeCache } = useRange(() => getItems(1));
  const cf = useCardFilter(rowData, range, rangeCache);
  const getItems = async (newPageNo = pagination?.currentPage) => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', newPageNo || 1);
      params.append('PageSize', pageSize || 20);
      params.append('Community', formData?.community || '');
      params.append('PropertyName', formData?.propertyName || '');
      const res = await fetchRanged({ apiService, endpoint: 'GET_PROPERTY_LIST', params, range, cache: rangeCache, pageNo: newPageNo, pageSize });
      setLoading(false);
      setRowData(res?.data?.result || []);
      setPagination(res.data.paginationData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const emptyValueFormatter = (params) => {
    const v = params.value;
    if (v === null || v === undefined || v === '' || v === '0') return '--';
    return v;
  };

  const displayValue = (row, field) => emptyValueFormatter({ value: row?.[field] });

  const buildFilterParams = (field) => ({
    getValues: () => rowData.map((r) => displayValue(r, field)),
    selected: columnFilters[field],
    onChange: (vals) => setColumnFilters((prev) => ({ ...prev, [field]: vals })),
    onClear: () =>
      setColumnFilters((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      }),
  });

  const filteredRowData = useMemo(() => {
    const activeFields = Object.keys(columnFilters).filter((f) => columnFilters[f] && columnFilters[f].length);
    if (!activeFields.length) return rowData;
    return rowData.filter((row) => activeFields.every((field) => columnFilters[field].includes(displayValue(row, field))));
  }, [rowData, columnFilters]);

  const columnDefs = [
    {
      headerName: t('common.actions'),
      field: 'scheduleId',
      cellRenderer: BtnCellRenderer,
      pinned: 'left',
      cellRendererParams: {
        onView: (rowsData) => handleView(rowsData),
        onEdit: (rowsData) => handleEdit(rowsData),
        onDelete: (rowsData) => handleDelete(rowsData),
        canDelete: isAdmin,
      },
      suppressMovable: true,
      sortable: false,
      filter: false,
      width: 140,
    },
    {
      headerName: t('propertyMaster.columns.propertyNo'),
      field: 'property_No',
      suppressMovable: true,
      sortable: true,
      width: 190,
      pinned: 'left',
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('property_No'),
    },
    {
      headerName: t('propertyMaster.columns.name'),
      field: 'description',
      suppressMovable: true,
      sortable: true,
      width: 300,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('description'),
    },
    {
      headerName: t('propertyMaster.columns.type'),
      field: 'property_Type',
      suppressMovable: true,
      sortable: true,
      width: 150,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('property_Type'),
    },
    {
      headerName: t('propertyMaster.columns.category'),
      field: 'property_Category',
      suppressMovable: true,
      sortable: true,
      width: 150,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('property_Category'),
    },
    {
      headerName: t('propertyMaster.columns.country'),
      field: 'country',
      suppressMovable: true,
      sortable: true,
      width: 200,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('country'),
    },
    {
      headerName: t('propertyMaster.columns.city'),
      field: 'city',
      suppressMovable: true,
      sortable: true,
      width: 170,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('city'),
    },
    {
      headerName: t('propertyMaster.columns.status'),
      field: 'project_Status',
      suppressMovable: true,
      sortable: true,
      width: 160,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('project_Status'),
    },
  ];

  const handlenew = () => {
    setFormModal({ mode: 'create', data: null });
  };
  const handleEdit = (rowsData) => {
    if (rowsData) {
      setFormModal({ mode: 'edit', data: rowsData });
    }
  };
  const handleView = (rowsData) => {
    if (rowsData) {
      setFormModal({ mode: 'view', data: rowsData });
    }
  };
  const handleDelete = (rowsData) => {
    if (rowsData) {
      setSelectedRow(rowsData);
      setPreviewModal(true);
    }
  };
  const closeFormModal = () => {
    setFormModal({ mode: null, data: null });
  };
  const handleFormSaved = () => {
    closeFormModal();
    getItems(1);
  };
  const handleClearFilters = () => {
    setFormData({
      community: '',
      propertyName: '',
    });
    setColumnFilters({});
    getItems(1);
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const exportPDF = () => {
    if (!filteredRowData?.length) {
      alert('No data available to export');
      return;
    }
    const doc = new jsPDF('landscape');
    const primaryColor = [110, 81, 153]; // #6E5199

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 297, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('PROPERTY LIST REPORT', 148.5, 15, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generated On: ${new Date().toLocaleDateString()}`, 280, 21, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    const tableColumn = ['Property No', 'Name', 'Type', 'Category', 'Country', 'City', 'Status'];
    const tableRows = filteredRowData.map((item) => [
      item.property_No || '-',
      item.description || '-',
      item.property_Type || '-',
      item.property_Category || '-',
      item.country || '-',
      item.city || '-',
      item.project_Status || '-',
    ]);

    doc.autoTable({
      startY: 35,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, valign: 'middle', lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 246, 252] },
      bodyStyles: { textColor: [50, 50, 50] },
      margin: { left: 10, right: 10 },
      didDrawPage: function (data) {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 5);
        doc.text('Property List', doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 5);
      },
    });
    doc.save('Property_List.pdf');
  };

  return (
    <div className="property-list-page cl-page">
      <div className="cl-titlebar">
        <div className="cl-title">
        <h1>{t('propertyMaster.pageTitle')}</h1>
        <p>Search, review and manage records.</p>
      </div>
        <RangeChips value={range} onChange={setRange} />
      </div>
      <SummaryStrip
        label="Total properties"
        total={totalFrom(pagination, rowData.length)}
        subtitle={`${countDistinct(cf.statsRows, 'city')} cities`}
        heroNote={cf.inPeriod ? 'Chosen period' : 'All records'} heroActive={!cf.filter} onHero={() => cf.setFilter(null)}
        tiles={[
          { label: cf.inPeriod ? 'In period' : 'On this page', value: cf.statsRows.length, note: 'properties', active: !cf.filter, onClick: () => cf.setFilter(null) },
          ...valueTiles(cf, 'project_Status', 2),
          ...valueTiles(cf, 'property_Type', 2),
          { label: 'Countries', value: countDistinct(cf.statsRows, 'country') },
        ]}
        ariaLabel="Property summary"
      />
      <section className="cl-panel cl-filters" aria-label={t('common.searchParameters')}>
        <div>
          <div>
            <div className="d-flex gap-3 align-items-stretch">
              <div className="form-group grid-contain flex-grow-1">
                <div className="d-flex">
                  <label className="form-label pt-2 start">{t('propertyMaster.filters.community')}</label>
                  <input type="text" className="p-2 form-control w-75" name="community" value={formData.community} onChange={handleInputChange} />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-2 start">{t('propertyMaster.filters.propertyName')}</label>
                  <input type="text" className="p-2 form-control w-75" name="propertyName" value={formData.propertyName} onChange={handleInputChange} />
                </div>
              </div>
              <div className="property-search-actions">
                <button type="button" className="cl-btn cl-btn-primary" onClick={() => getItems()}><FiSearch size={16} />{t('common.search')}</button>
                <button type="button" className="cl-btn cl-btn-ghost" onClick={() => handleClearFilters()}><FiRotateCcw size={16} />{t('common.reset')}</button>
                <button type="button" className="cl-btn cl-btn-ghost" onClick={() => exportPDF()}><FiDownload size={16} />{t('common.export')}</button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="cl-panel cl-list">
        {loading ? (
          <div>{t('common.loading')}</div>
        ) : (
          <>
            <div className="cl-list-head">
              <h2>{t('propertyMaster.pageTitle')}</h2>
              <button type="button" className="cl-btn cl-btn-primary" onClick={() => handlenew()}><FiPlus size={16} />{t('common.add')}</button>
            </div>
            <div className="cl-grid-wrap">
              <div className="ag-theme-quartz property-grid customer-grid" style={{ height: 650 }}>
                <AgGridReact
                  rowData={cf.filter ? cf.shownRows : filteredRowData}
                  enableFilter={true}
                  floatingFilter={true}
                  columnDefs={columnDefs}
                  frameworkComponents={{ BtnCellRenderer }}
                />
              </div>
              {!cf.filter && <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={getItems} />}
            </div>
          </>
        )}
      </section>

      <Modal show={previewModal} size="md" onHide={() => setPreviewModal(false)} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{t('propertyMaster.deleteTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Modal.Title>{t('propertyMaster.deleteConfirm', { name: selectedRow?.description })}</Modal.Title>
        </Modal.Body>
        <Modal.Footer className="end p-3">
          <button className="mainbtnRed" onClick={() => setPreviewModal(false)}>
            {t('common.cancel')}
          </button>
          <button className="mainbtn">{t('common.delete')}</button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!formModal.mode}
        onHide={closeFormModal}
        backdrop="static"
        keyboard={false}
        centered
        dialogClassName="property-form-modal"
        contentClassName="p-0"
      >
        <Modal.Body style={{ padding: 0 }}>
          {formModal.mode === 'create' && <PropertyMasterCreate onClose={closeFormModal} onSaved={handleFormSaved} />}
          {(formModal.mode === 'edit' || formModal.mode === 'view') && (
            <PropertyMasterUpdate
              initialData={formModal.data}
              viewOnly={formModal.mode === 'view'}
              onClose={closeFormModal}
              onSaved={handleFormSaved}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};
export default PropertyListing;
const BtnCellRenderer = RowActions;
